import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { api, erroMsg } from "../../lib/api";
import { parsePctInput, toPctInput } from "../../lib/format";
import { useMeta } from "../../context/MetaContext";
import { dataHora } from "../../lib/format";
import { Copy, Loader2, RotateCcw, Save, Wand2 } from "lucide-react";

export default function AdminImpostos() {
  const { ncms, ufs, enquadramentos } = useMeta();
  const [ncm, setNcm] = useState("");
  const [grid, setGrid] = useState({}); // { UF: { ENQ: "25,22" } }
  const [original, setOriginal] = useState({});
  const [descricao, setDescricao] = useState("");
  const [atualizadoEm, setAtualizadoEm] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [aplicarTodos, setAplicarTodos] = useState({ aberto: false, enq: "", valor: "" });

  useEffect(() => {
    if (!ncm && ncms.length) setNcm(ncms[0].codigo);
  }, [ncms, ncm]);

  const carregar = useCallback(async () => {
    if (!ncm) return;
    setCarregando(true);
    try {
      const r = await api.get(`/admin/taxes/${ncm}`);
      const g = {};
      Object.entries(r.data.grid).forEach(([uf, linha]) => {
        g[uf] = {};
        Object.entries(linha).forEach(([enq, v]) => {
          g[uf][enq] = toPctInput(v, 4);
        });
      });
      setGrid(g);
      setOriginal(JSON.parse(JSON.stringify(g)));
      setDescricao(r.data.descricao || "");
      setAtualizadoEm(r.data.atualizado_em);
    } catch (e) {
      toast.error(erroMsg(e));
    } finally {
      setCarregando(false);
    }
  }, [ncm]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const alterado = useMemo(
    () => JSON.stringify(grid) !== JSON.stringify(original),
    [grid, original]
  );

  const setCelula = (uf, enq, valor) =>
    setGrid((g) => ({ ...g, [uf]: { ...g[uf], [enq]: valor } }));

  const salvar = async () => {
    const aliquotas = {};
    for (const uf of Object.keys(grid)) {
      aliquotas[uf] = {};
      for (const enq of Object.keys(grid[uf])) {
        const f = parsePctInput(grid[uf][enq]);
        if (f < 0 || f > 1) {
          toast.error(`Alíquota inválida em ${uf}: use valores entre 0 e 100%.`);
          return;
        }
        aliquotas[uf][enq] = f;
      }
    }
    setSalvando(true);
    try {
      await api.put(`/admin/taxes/${ncm}`, { aliquotas });
      toast.success(`Alíquotas do NCM ${ncm} atualizadas`);
      carregar();
    } catch (e) {
      toast.error(erroMsg(e));
    } finally {
      setSalvando(false);
    }
  };

  const copiarDe = (ufOrigem) => {
    if (!grid[ufOrigem]) return;
    const linha = grid[ufOrigem];
    setGrid((g) => {
      const novo = { ...g };
      Object.keys(novo).forEach((uf) => {
        novo[uf] = { ...linha };
      });
      return novo;
    });
    toast.success(`Alíquotas de ${ufOrigem} replicadas para todos os estados`);
  };

  const aplicarColuna = () => {
    const { enq, valor } = aplicarTodos;
    if (!enq) return;
    setGrid((g) => {
      const novo = { ...g };
      Object.keys(novo).forEach((uf) => {
        novo[uf] = { ...novo[uf], [enq]: valor };
      });
      return novo;
    });
    setAplicarTodos({ aberto: false, enq: "", valor: "" });
    toast.success("Valor aplicado a todos os estados");
  };

  return (
    <AdminShell
      titulo="Matriz de impostos (ICMS-ST / DIFAL)"
      descricao="Selecione o NCM e edite as alíquotas por estado de destino e enquadramento do cliente. Valores em porcentagem."
      acoes={
        <>
          <Button
            variant="outline"
            onClick={() => setGrid(JSON.parse(JSON.stringify(original)))}
            disabled={!alterado}
            data-testid="admin-impostos-desfazer"
          >
            <RotateCcw className="mr-1.5 h-4 w-4" /> Desfazer
          </Button>
          <Button onClick={salvar} disabled={!alterado || salvando} data-testid="admin-impostos-salvar-button">
            {salvando ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1.5 h-4 w-4" />
            )}
            Salvar alíquotas
          </Button>
        </>
      }
    >
      <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3.5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <div className="w-full space-y-1.5 lg:w-72">
            <Label>NCM</Label>
            <Select value={ncm} onValueChange={setNcm}>
              <SelectTrigger className="h-11" data-testid="admin-impostos-ncm-select">
                <SelectValue placeholder="Selecione o NCM" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {ncms.map((n) => (
                  <SelectItem key={n.codigo} value={n.codigo}>
                    {n.codigo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Descrição do NCM
            </div>
            <p className="line-clamp-2 text-[12.5px] leading-relaxed text-slate-600">
              {descricao || "—"}
            </p>
            {atualizadoEm && (
              <div className="mt-1 text-[11px] text-slate-400">
                Atualizado em {dataHora(atualizadoEm)}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAplicarTodos({ aberto: true, enq: "REVENDA", valor: "" })}
              data-testid="admin-impostos-aplicar-todos"
            >
              <Wand2 className="mr-1.5 h-4 w-4" /> Aplicar a todos os estados
            </Button>
            <Select onValueChange={copiarDe}>
              <SelectTrigger className="h-9 w-[190px]" data-testid="admin-impostos-copiar-de">
                <SelectValue placeholder="Replicar linha de…" />
              </SelectTrigger>
              <SelectContent className="max-h-72 bg-white">
                {ufs.map((u) => (
                  <SelectItem key={u.sigla} value={u.sigla}>
                    Copiar de {u.sigla}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {alterado && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
            Existem alterações não salvas nesta matriz.
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {carregando ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : (
          <div className="max-h-[68vh] overflow-auto">
            <table className="w-full border-collapse text-sm" data-testid="admin-impostos-grid">
              <thead>
                <tr>
                  <th className="sticky left-0 top-0 z-20 border-b border-r border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    Estado
                  </th>
                  {enquadramentos.map((e) => (
                    <th
                      key={e.codigo}
                      className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-600"
                    >
                      {e.curto}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ufs.map((u) => (
                  <tr key={u.sigla} className="even:bg-slate-50/60">
                    <td className="sticky left-0 z-10 border-b border-r border-slate-200 bg-white px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono-num text-[11px]">
                          {u.sigla}
                        </Badge>
                        <span className="whitespace-nowrap text-[12.5px] text-slate-700">
                          {u.nome}
                        </span>
                      </div>
                    </td>
                    {enquadramentos.map((e) => (
                      <td key={e.codigo} className="border-b border-slate-100 px-2 py-1.5">
                        <div className="relative">
                          <Input
                            inputMode="decimal"
                            value={grid[u.sigla]?.[e.codigo] ?? "0"}
                            onChange={(ev) => setCelula(u.sigla, e.codigo, ev.target.value)}
                            className="h-9 pr-6 text-right num"
                            data-testid={`admin-impostos-cell-${u.sigla}-${e.codigo}`}
                          />
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">
                            %
                          </span>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="mt-3 text-[12px] leading-relaxed text-slate-500">
        O valor de ST/DIFAL é calculado sobre (valor líquido + IPI). Alíquota 0% significa que não
        há incidência naquele estado/enquadramento. Todos os produtos com este NCM passam a usar
        estas alíquotas imediatamente.
      </p>

      <Dialog
        open={aplicarTodos.aberto}
        onOpenChange={(o) => setAplicarTodos((a) => ({ ...a, aberto: o }))}
      >
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Aplicar valor a todos os estados</DialogTitle>
            <DialogDescription>
              Define a mesma alíquota para os 27 estados em um enquadramento específico.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Enquadramento</Label>
              <Select
                value={aplicarTodos.enq}
                onValueChange={(v) => setAplicarTodos((a) => ({ ...a, enq: v }))}
              >
                <SelectTrigger className="h-10" data-testid="admin-impostos-massa-enq">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {enquadramentos.map((e) => (
                    <SelectItem key={e.codigo} value={e.codigo}>
                      {e.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Alíquota (%)</Label>
              <Input
                inputMode="decimal"
                value={aplicarTodos.valor}
                onChange={(e) => setAplicarTodos((a) => ({ ...a, valor: e.target.value }))}
                placeholder="Ex.: 13"
                className="h-10 num"
                data-testid="admin-impostos-massa-valor"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAplicarTodos({ aberto: false, enq: "", valor: "" })}
            >
              Cancelar
            </Button>
            <Button onClick={aplicarColuna} data-testid="admin-impostos-massa-aplicar">
              <Copy className="mr-1.5 h-4 w-4" /> Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
